import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './login.module.scss';
import logo from 'src/components/auth-page-layout/logo.png';
import { TextInput, TextInputValue } from '../../../components/text-input/TextInput';
import Checkbox from '../../../components/checkbox/Checkbox';
import { useCheckAccessQuery, useRegisterParentMutation } from 'src/api/graphql-codegen';
import { useQueryParams } from 'src/hooks/useQueryParams';
import { VideoPlayer } from '@components';
import Button from 'src/components/button/Button';
import Spinner from 'src/components/spinner/Spinner';
import { validateEmail } from 'src/utils/validation';
import { useAuthToken } from 'src/hooks/useAuthToken';
import './videoJs.style.css'
import { useAnalytics } from 'src/hooks/useAnalytics';
import { Events } from 'src/services/Events';
import { setLogoutEventsSent } from 'src/utils/logoutTrackingGuard';
import { getOrCreateSessionId } from 'src/utils/sentryTrackingConfig';
import { ParentLoginPageVideoLink } from '@constants';
import { logger } from '../../../utils/logger';

const emptyErrors = {
  email: null,
  tou: null,
};

function Login() {
  const id = useQueryParams().get('id') || '';
  const resource = useQueryParams().get('resource');

  logger.info('----- resource: ', resource)
  const {data, loading, error} = useCheckAccessQuery({ variables: { input: { id } }, skip: !id });
  const [isInvalidId, setInvalidId] = useState(false);

  const isMobile = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const regexs = [/(Android)(.+)(Mobile)/i, /BlackBerry/i, /iPhone|iPod/i, /Opera Mini/i, /IEMobile/i]
    return regexs.some((b) => userAgent.match(b))
  }, [])

  useEffect(() => {
    if ((data && !data.checkAccess) || error) {
      setInvalidId(true);
    }
  }, [data, error])

  const content = ( (
      <div className={styles.rightDiv}>
        <div className={styles.logoWrapper}>
        </div>
        <div className={styles.rightContent}>
          <div className={styles.loginTitle}>{'Access Resources'}</div>  
          <RegisterFormContent id={id} resource={resource} />
        </div>
      </div>
    )
  )

  const description = (
    <>
      <div style={{marginBottom: 30}}><p>Your child’s school is using the Open Parachute program to teach well-being
        skills.
        To support their learning,
        we have put together a series of resources for you so you can reinforce these skills
        at home.</p></div>
      <div style={{marginBottom: 30}}><p style={{marginBottom: 10}}>Our aim is to help your child achieve success in
        every area of their life!</p></div>
      <p>Hear from Open Parachute Founder, Dr Hayley Watson:</p>
    </>
  )

  const normalViewContent = (
    <div className={styles.page}>
      <div className={styles.left}>
        <div className={styles.leftContent}>
          <div className={styles.leftContentContainerTop}>
            <h1 className={styles.title}>{'Welcome Parents!'}</h1>
          </div>
          <div className={styles.leftContentContainerBottom}>
            <h5 className={styles.description}>
              {description}
            </h5>
          </div>
          <div className={styles.videoWrapper}>
            <VideoPlayer isCoverVideo src={ParentLoginPageVideoLink} className={styles.video} autoPlay={false} metaData={{video_title: 'Parent Description'}}/>
          </div>
        </div>
      </div>
      <div className={styles.right}>
        {content}
        {/* <div className={styles.footer}>
          © {(new Date().getFullYear()) || '2022'} Open Parachute Ltd. All rights reserved.
        </div> */}
      </div>
    </div>
  )

  const mobileViewContent = (
    <div className={styles.mobilePage}>
      {/* <div className={styles.mobileHeader}>
        <img className={styles.mobileLogo} src={logo} alt="Logo"/>
      </div> */}
      <div className={styles.mobileMiddle}>
        <span className={styles.mobileTitle}>{'Welcome Parents!'}</span>
        <div className={styles.mobileDescription}>
          {description}
          <p className={styles.mobileDescriptionUnderline}>Scroll below video to access free resources.</p>
          <p>Hear from Open Parachute Founder, Dr. Watson:</p>
        </div>
        <VideoPlayer isCoverVideo src={ParentLoginPageVideoLink} className={styles.video} startTime={0} autoPlay={true} metaData={{video_title: 'Parent Description'}}/>
      </div>
      <div className={styles.mobileRegistration}>
        {/* <h1>Registration</h1> */}
        {isInvalidId ? (<div className={styles.loginTitle}>This link is invalid or expired.</div>) : (loading ? (
          <div className={styles.spinner}><Spinner/></div>) : 
          <div className={styles.rightDiv}>
            <div className={styles.logoWrapper}></div>
            <div className={styles.rightContent}>
              <div className={styles.loginTitle}>{'Access Resources'}</div>  
              <RegisterFormContent id={id} resource={resource}  loading={loading} />
            </div>
          </div>
        )}
      </div>
      {/* <div className={styles.mobileFooter}>
        © Copyright Open Parachute {(new Date().getFullYear()) || '2022'}
      </div> */}
    </div>
  )

  return isMobile ? mobileViewContent : normalViewContent;
}

const RegisterFormContent = ({id, loading, resource}: { id: string, resource: string | null; loading?: boolean }) => {
  const history = useHistory();
  const {setParentAuthToken, setParentData} = useAuthToken();
  const [loginParent, {loading: parentRegisterLoading}] = useRegisterParentMutation();
  const [email, setEmail] = useState('');
  const [tou, setTou] = useState(false);
  const [errors, setErrors] = useState<{ email: string | null; tou: string | null; }>(emptyErrors);
  const { segment, trackEvent, identify } = useAnalytics();

  const handleChangeTou = useCallback((v: boolean) => {
    setTou(v);
    setErrors({
      ...errors,
      tou: null,
    });
  }, [errors]);

  const handleEmailChange = useCallback((value: TextInputValue) => {
    setEmail(value as string);
    setErrors({
      ...errors,
      email: null,
    });
  }, [errors]);

  const handleSubmit = useCallback(async () => {
    const error = validateEmail(email);
    if (error) {
      return setErrors({email: error, tou: null});
    }
    if (!tou) {
      return setErrors({email: null, tou: 'Please accept the Terms to proceed'});
    }
    try {
      const result = await loginParent({ variables: { input: { email, accessId: id } } });
      if (result && result.data?.registerParent) {
        const res = result.data?.registerParent;
        setLogoutEventsSent(false);
        setParentAuthToken(res?.token);
        setParentData({email, accessId: id});
        if (res?.token && segment?.initialized) {
          try {
            await segment?.identify?.(`PA_${res?.parentId}`, {
              $user_id: `PA_${res?.parentId}`,
              isParent: true,
              accessId: id,
              special_course_id: res?.specialCourseId,
              account: res?.schoolName,
              school_name: res?.schoolName,
              school_id: res?.schoolId,
            });
            identify(`PA_${res?.parentId}`, {
              $user_id: `PA_${res?.parentId}`,
              isParent: true,
              accessId: id,
              special_course_id: res?.specialCourseId,
              account: res?.schoolName,
              school_name: res?.schoolName,
              school_id: res?.schoolId,
            });
            await trackEvent(Events.actions.parentLoggedIn, {
              parent_user_id: `PA_${res?.parentId}`,
              accessId: id,
              account: res?.schoolName,
              school_name: res?.schoolName,
              schoolId: res?.schoolId,
              schoolName: res?.schoolName,
            }, true);
            const sessionId = getOrCreateSessionId();
            const timestamp = new Date().toISOString();
            await trackEvent(Events.actions.sessionStarted, {
              parent_user_id: `PA_${res?.parentId}`,
              accessId: id,
              account: res?.schoolName,
              reason: 'parent_login',
              sessionId,
              timestamp,
            });
          } catch (e: any) {
            logger.info('error--------------------', e);
          }
        }
        if (res?.token) {
          if (id && id !== '' && id.length > 20) {
            history.replace(`/parent/dashboard?specialCourseId=${res.specialCourseId}`);
          } else {
            history.replace(resource ? `/parent/dashboard?lid=${resource}` : '/parent/dashboard');
          }
        }
      }
    } catch (e: any) {
      const errorMessage = e?.graphQLErrors?.[0]?.message || e?.message;
      if (errorMessage && typeof errorMessage === 'string') {
        setErrors({email: errorMessage, tou: null});
      } else {
        setErrors({email: null, tou: null});
      }
    }
  }, [email, history, id, loginParent, resource, segment, setParentAuthToken, setParentData, tou, trackEvent, identify]);

  return (
    <>
      <TextInput allowSpecialCharacters className={styles.input} onChange={handleEmailChange} value={email}
                 label={'Please provide your email address to access resources:'} placeholder={'Email'} id={'email'}/>
      {!!errors.email && <div className={styles.error}>{errors.email}</div>}
      <div className={styles.touWrapper}>
        <Checkbox id={'tou'} checked={tou} onChange={handleChangeTou}>
          <span className={styles.touText}>I have read and agree to the <a className={styles.touLink} onClick={() => {
            window.open(
              "https://openparachuteschools.com/terms-of-use", "_blank")
          }} target="_blank" rel="noreferrer" href="https://openparachuteschools.com/terms-of-use">Terms of Use</a></span>
        </Checkbox>
        {!!errors.tou && <div className={styles.error}>{errors.tou}</div>}
      </div>
      <Button disabled={parentRegisterLoading || loading} className={styles.smallSubmit} label="Continue" onClick={handleSubmit}/>
    </>
  )
};

export default Login;
